import React from 'react';
import { IWebsiteWidgetConfig } from '../../../interfaces/website_widget';

type Option = {
    parameter: keyof IWebsiteWidgetConfig;
    required: boolean;
    default: string;
    acceptedValues: string;
    description: string;
};

const options: Option[] = [
    {
        parameter: 'layout',
        required: true,
        default: 'vertical',
        acceptedValues: 'vertical or horizontal',
        description: 'The general layout of your widget.'
    },
    {
        parameter: 'nhid',
        required: true,
        default: 'nhid',
        acceptedValues: 'Numeric',
        description: 'The ID of your Organization or Facility.'
    },
    {
        parameter: 'showReviewScore',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show your or hide your review score.'
    },
    {
        parameter: 'showRatings',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide your star ratings.'
    },
    {
        parameter: 'showWriteReviewButton',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide the write a review button.'
    },
    {
        parameter: 'widthFull',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'If selected, your widget will fill the space where available on your website.'
    },
    {
        parameter: 'width',
        required: false,
        default: 'N/A',
        acceptedValues: 'Numeric',
        description: 'Set your widget to be a specific width on your website.'
    },
    {
        parameter: 'reviewCount',
        required: false,
        default: '10',
        acceptedValues: 'Numeric',
        description: 'Set the amount of reviews that you want your widget to display.'
    },
    {
        parameter: 'showLogo',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide the Nursing Homes logo.'
    },
    {
        parameter: 'backgroundColor',
        required: false,
        default: 'light-blue',
        acceptedValues: 'transparent, white, light-blue, light-green, light-grey',
        description: 'Set the background color of your widget.'
    },
    {
        parameter: 'showFacilityName',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show your facility name on your widget.'
    },
    {
        parameter: 'showReviews',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide reviews on your widget.'
    },
    {
        parameter: 'showReadAllReviewsLink',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide the read all reviews link.'
    },
    {
        parameter: 'slideShow',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show your reviews in a slide show.'
    },
    {
        parameter: 'slideShowReviewsCount',
        required: false,
        default: '2',
        acceptedValues: 'Numeric between 1-4',
        description: 'How many reviews should the slide show display at a time.'
    },
    {
        parameter: 'minReviewRating',
        required: false,
        default: '3',
        acceptedValues: 'Numeric between 2-5',
        description: 'The minimum rating a review should have to be displayed.'
    },
    {
        parameter: 'showDate',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide the published date of the review.'
    },
    {
        parameter: 'showReviewSnippet',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show a snippet of the review content. If not selected the full review will be displayed.'
    },
    {
        parameter: 'showReviewerAndConnection',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'Show or hide the reviewer\'s connection to the resident.'
    },
    {
        parameter: 'border',
        required: false,
        default: 'true',
        acceptedValues: 'Boolean',
        description: 'If selected, the widget will have a border.'
    },
    {
        parameter: 'noFollowLinks',
        required: false,
        default: 'false',
        acceptedValues: 'Boolean',
        description: 'If selected, all links will have a nofollow tag.'
    },
    {
        parameter: 'marginTop',
        required: false,
        default: '10',
        acceptedValues: 'Numeric',
        description: 'Manages the margin at the top of the widget.'
    },
    {
        parameter: 'marginBottom',
        required: false,
        default: '10',
        acceptedValues: 'Numeric',
        description: 'Manages the margin at the bottom of the widget.'
    },
    {
        parameter: 'marginLeft',
        required: false,
        default: '10',
        acceptedValues: 'Numeric',
        description: 'Manages the margin to the left of the widget.'
    },
    {
        parameter: 'marginRight',
        required: false,
        default: '10',
        acceptedValues: 'Numeric',
        description: 'Manages the margin to the right of the widget.'
    },
];

export default function WebsiteWidgetConfigTable() {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left rtl:text-right">
                <thead className="text-xs uppercase bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3">Parameter</th>
                        <th scope="col" className="px-6 py-3">Required</th>
                        <th scope="col" className="px-6 py-3">Default</th>
                        <th scope="col" className="px-6 py-3">Accepted Values</th>
                        <th scope="col" className="px-6 py-3">Description</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        options.map((option: Option, index: number) => (
                            <tr
                                className="odd:bg-white even:bg-gray-50 border-b border-gray-200"
                                key={index}
                            >
                                <td  className="px-6 py-4">{option.parameter}</td>
                                <td  className="px-6 py-4">{option.required ? 'Yes': 'No'}</td>
                                <td  className="px-6 py-4">{option.default}</td>
                                <td  className="px-6 py-4">{option.acceptedValues}</td>
                                <td  className="px-6 py-4">{option.description}</td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>            
        </div>
    );
}
